// WhatsApp operations — prototype-faithful (chitos-prototype screens4.jsx).
// Real data via orpc.whatsapp.list_threads + list_messages. The KPI strip
// (sent/delivered/read/failed) and inline send + broadcast/templates from
// the prototype need backend endpoints that don't exist yet (no wa.stats,
// sends go via the worker) — omitted/disabled rather than faked.

import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { current_language } from '../i18n/index.js';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

interface Subscriber { id: string; name_ta: string; name_en: string; phone: string }
interface WaEntry {
  id: string;
  direction: 'outbound' | 'inbound';
  kind: string;
  subscriber_phone: string;
  template_id: string | null;
  body: string | null;
  status: string;
  created_at: string;
}
interface Thread {
  subscriber_phone: string;
  subscriber: Subscriber | null;
  last_message: WaEntry;
  message_count: number;
}

function initials(name: string | null | undefined, phone: string): string {
  const src = name && name.trim() ? name : phone;
  const p = src.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '··';
  if (p.length === 1) return p[0]!.slice(-2).toUpperCase();
  return (p[0]!.charAt(0) + p[p.length - 1]!.charAt(0)).toUpperCase();
}

const DELIVERED = new Set(['delivered', 'read']);

export function WhatsAppPage(): ReactElement {
  const lang = current_language();
  const qc = useQueryClient();
  const [selected, set_selected] = useState<string | null>(null);
  const [draft, set_draft] = useState('');
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  const threads_q = useQuery({
    queryKey: ['wa-threads'],
    queryFn: () => orpc.whatsapp.list_threads({}),
  });
  const threads = (threads_q.data?.threads ?? []) as Thread[];

  // Daily KPI strip — sent/delivered/read/failed for today.
  const stats_q = useQuery({
    queryKey: ['wa-daily-stats'],
    queryFn: () => orpc.whatsapp.daily_stats({}),
    refetchInterval: 60_000,
  });

  // Inline send — uses notify.send with channels=['whatsapp'] + the selected
  // thread's subscriber. Fire-and-forget at the page level — toast surfaces
  // failure but the thread list refetches on success.
  const selected_thread = threads.find((th) => th.subscriber_phone === selected);
  const send_m = useMutation({
    mutationFn: () => {
      if (!selected_thread?.subscriber?.id) throw new Error('no subscriber for thread');
      return orpc.notify.send({
        subscriber_id: selected_thread.subscriber.id,
        intent: 'custom',
        channels: ['whatsapp'],
        custom_body_text: draft.trim(),
      });
    },
    onSuccess: () => {
      set_draft('');
      void qc.invalidateQueries({ queryKey: ['wa-messages', selected] });
      void qc.invalidateQueries({ queryKey: ['wa-threads'] });
    },
  });

  // Default to the first thread once threads load.
  useEffect(() => {
    if (!selected && threads.length > 0) set_selected(threads[0]!.subscriber_phone);
  }, [threads, selected]);

  const messages_q = useQuery({
    queryKey: ['wa-messages', selected],
    queryFn: () =>
      orpc.whatsapp.list_messages(selected ? { subscriber_phone: selected, limit: 100 } : { limit: 100 }),
    enabled: !!selected,
  });
  const messages = (messages_q.data?.messages ?? []) as WaEntry[];
  const conv = threads.find((th) => th.subscriber_phone === selected) ?? null;

  return (
    <div className="page full" style={{ padding: 0, height: 'calc(100vh - 52px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="page-head" style={{ flexShrink: 0, margin: 0, padding: '8px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)' }}>{t('வாட்ஸ்அப்', 'WhatsApp')}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t('Plivo வணிக API', 'Plivo Business API')}</div>
        </div>
        {stats_q.data && (
          <div style={{ display: 'flex', gap: 14, fontSize: 11 }}>
            {(['sent', 'delivered', 'read', 'failed'] as const).map((k) => (
              <div key={k} style={{ textAlign: 'right' }}>
                <div style={{ color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10 }}>
                  {k}
                </div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 600, color: k === 'failed' && stats_q.data[k] > 0 ? 'var(--bad)' : 'var(--ink)' }}>
                  {stats_q.data[k]}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', padding: '6px 22px 14px' }}>
        <div className="card" style={{ overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="wa-stage">
            {/* Conversation list */}
            <div className="wa-side">
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)', fontSize: 11, color: 'var(--ink-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('உரையாடல்கள்', 'Conversations')} {threads.length > 0 && `(${threads.length})`}
              </div>
              {threads_q.isLoading && <div style={{ padding: 14, fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>}
              {!threads_q.isLoading && threads.length === 0 && (
                <div style={{ padding: 14, fontSize: 13, color: 'var(--ink-3)' }}>{t('உரையாடல்கள் இல்லை', 'No conversations yet')}</div>
              )}
              {threads.map((th) => {
                const name = (lang === 'ta' ? th.subscriber?.name_ta : th.subscriber?.name_en) ?? th.subscriber_phone;
                const preview = th.last_message.body ?? `[${th.last_message.template_id ?? th.last_message.kind}]`;
                return (
                  <div
                    key={th.subscriber_phone}
                    className={'wa-side-row' + (selected === th.subscriber_phone ? ' on' : '')}
                    onClick={() => set_selected(th.subscriber_phone)}
                  >
                    <div className="ava-sm">{initials(lang === 'ta' ? th.subscriber?.name_ta : th.subscriber?.name_en, th.subscriber_phone)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span className={lang === 'ta' ? 'tamil' : ''} style={{ fontSize: 12.5, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {name}
                        </span>
                        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{th.message_count}</span>
                      </div>
                      <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontSize: 11.5, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                        {preview}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Conversation stream */}
            <div className="wa-conv">
              {!conv && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                  {threads.length === 0 ? t('உரையாடல் இல்லை', 'No conversations') : t('ஒரு உரையாடலைத் தேர்ந்தெடுக்கவும்', 'Select a conversation')}
                </div>
              )}
              {conv && (
                <>
                  <div className="wa-conv-head">
                    <div className="ava-sm">{initials(lang === 'ta' ? conv.subscriber?.name_ta : conv.subscriber?.name_en, conv.subscriber_phone)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontWeight: 500 }}>
                        {(lang === 'ta' ? conv.subscriber?.name_ta : conv.subscriber?.name_en) ?? conv.subscriber_phone}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }} className="mono">{conv.subscriber_phone}</div>
                    </div>
                  </div>
                  <div className="wa-stream">
                    {messages_q.isLoading && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>}
                    {messages
                      .slice()
                      .reverse()
                      .map((m) => (
                        <div key={m.id} className={'wa-bubble ' + (m.direction === 'outbound' ? 'out' : '')}>
                          <span className={lang === 'ta' ? 'tamil' : ''}>
                            {m.body ?? `[${m.template_id ?? m.kind}]`}
                          </span>
                          <div className="meta">
                            {new Date(m.created_at).toLocaleTimeString(lang === 'ta' ? 'ta-IN' : 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                            {m.direction === 'outbound' && DELIVERED.has(m.status) && (
                              <span style={{ color: 'var(--good)' }}> ✓✓</span>
                            )}
                          </div>
                        </div>
                      ))}
                    <div style={{ alignSelf: 'center', fontSize: 10.5, color: 'var(--ink-3)', padding: 4 }}>
                      {t('வாட்ஸ்அப் வணிக API · Plivo', 'WhatsApp Business API · Plivo')}
                    </div>
                  </div>
                  <div className="wa-input">
                    <input
                      value={draft}
                      onChange={(e) => set_draft(e.target.value)}
                      disabled={!selected_thread?.subscriber?.id || send_m.isPending}
                      placeholder={
                        selected_thread?.subscriber?.id
                          ? t('உங்கள் செய்தி…', 'Your message…')
                          : t('உறுப்பினர் தெரியவில்லை', 'Subscriber not on file')
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && draft.trim() && !send_m.isPending) {
                          send_m.mutate();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn primary sm"
                      disabled={!draft.trim() || !selected_thread?.subscriber?.id || send_m.isPending}
                      onClick={() => send_m.mutate()}
                    >
                      {send_m.isPending ? '…' : t('அனுப்பு', 'Send')}
                    </button>
                  </div>
                  {send_m.error && (
                    <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--bad)' }}>
                      {friendly_error(send_m.error)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
